// AnalyticsPage.js

import React, { useState, useEffect } from 'react';
import FilterPanel from './FilterPanel';
import ChartsPanel from './ChartsPanel';
import AnalyticsTable from './AnalyticsTable';
import { getAllClusters, getChartData  } from '../../api';
import Toast from '../Toast';
import './AnalyticsPage.css';

const AnalyticsPage = () => {
    // Выбранный завод для отображения аналитики на графиках
    const [selectedFactory, setSelectedFactory] = useState(null);
    // Выбранный показатель для графика (в городе / в городе+МВО / все)
    const [selectedIndicator, setSelectedIndicator] = useState('all');
    // Данные для построения графиков
    const [chartData, setChartData] = useState([]);
    // Список доступных кластеров и заводов
    const [factories, setFactories] = useState([]);
    // Текст сообщения тоста
    const [toastMessage, setToastMessage] = useState(null);
    // Тип тоста (успех / ошибка и т.п.)
    const [toastType, setToastType] = useState('');
    // Текущий режим просмотра: графики или таблица. Значение запоминается в localStorage
    const [viewMode, setViewMode] = useState(() => {
        const saved = localStorage.getItem('analyticsViewMode');
        return saved === 'table' ? 'table' : 'charts';
    }); // 'charts' | 'table'

    // Текущая дата (конец диапазона по умолчанию для графиков)
    const today = new Date();
    // Дата три года назад от сегодняшнего дня (начало диапазона по умолчанию для графиков)
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(today.getFullYear() - 3);

    // Форматирование даты в локальный формат YYYY-MM-DD без учета часового пояса
    const formatLocalDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Диапазон дат по умолчанию для графиков: последние 3 года
    const defaultChartStartDate = formatLocalDate(threeYearsAgo);
    const defaultChartEndDate = formatLocalDate(today);

    // Текущий выбранный диапазон дат для графиков
    const [chartStartDate, setChartStartDate] = useState(defaultChartStartDate);
    const [chartEndDate, setChartEndDate] = useState(defaultChartEndDate);

    useEffect(() => {
        // Загружаем список кластеров и заводов при первом открытии страницы
        getAllClusters(false)
            .then(response => {
                setFactories(response.data);
            })
            .catch(error => {
                console.error('Error fetching factories:', error);
                setToastMessage('Ошибка загрузки заводов');
                setToastType('error');
            });
    }, []);

    useEffect(() => {
        // Сохраняем выбранный режим отображения (графики / таблица), чтобы восстановить при следующем заходе
        localStorage.setItem('analyticsViewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        // Загружаем данные для графика при изменении завода или диапазона дат
        if (!selectedFactory || !chartStartDate || !chartEndDate) {
            return;
        }

        // Простейшая валидация диапазона дат
        if (chartStartDate > chartEndDate) {
            setToastMessage('Дата начала не может быть больше даты окончания');
            setToastType('error');
            return;
        }

        // Запрос на сервер за данными для графика по выбранному заводу и диапазону дат
        getChartData(selectedFactory.id, chartStartDate, chartEndDate)
            .then(data => {
                setChartData(Array.isArray(data) ? data : []);
            })
            .catch(error => {
                console.error('Error fetching chart data:', error);
                setToastMessage('Ошибка загрузки данных для графика');
                setToastType('error');
            });
    }, [selectedFactory, chartStartDate, chartEndDate]);


    return (
        <div style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="analytics-page__tabs">
                <button
                    className={`analytics-page__tab-button ${viewMode === 'charts' ? 'analytics-page__tab-button--active' : ''}`}
                    onClick={() => setViewMode('charts')}
                >
                    Графики
                </button>
                <button
                    className={`analytics-page__tab-button ${viewMode === 'table' ? 'analytics-page__tab-button--active' : ''}`}
                    onClick={() => setViewMode('table')}
                >
                    Таблица
                </button>
            </div>

            {viewMode === 'charts' && (
                <>
                    {/* Панель фильтров для графиков: выбор завода, показателя и диапазона дат */}
                    <FilterPanel
                        factories={factories}
                        onFactorySelect={setSelectedFactory}
                        onIndicatorChange={(e) => setSelectedIndicator(e.target.value)}
                        chartStartDate={chartStartDate}
                        chartEndDate={chartEndDate}
                        onDateRangeChange={(start, end) => {
                            setChartStartDate(start);
                            setChartEndDate(end);
                        }}
                    />

                    {/* Блок с графиками отображается только если выбран завод */}
                    {selectedFactory && (
                        <ChartsPanel
                            chartData={chartData}
                            selectedFactory={selectedFactory}
                            indicator={selectedIndicator}
                        />
                    )}
                </>
            )}

            {viewMode === 'table' && (
                <AnalyticsTable />
            )}

            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage(null)}
                />
            )}
        </div>
    );
};

export default AnalyticsPage;
